import { Command } from 'cmdk';
import React, { useMemo, useCallback } from 'react';
import type { IconName } from '@customerio/pluma-icons';
import {
  TextField,
  Box,
  ModalPrimitiveDialog,
  ModalPrimitiveOverlay,
  ModalPrimitive,
  EmptyStateDescription,
  EmptyState,
  EmptyStateTitle,
  Icon,
  Text,
  Flex,
  FormattedDateTime,
  EmptyStateGraphic,
  Spinner,
  KeyboardShortcut,
  InlineStack,
  type ModalPrimitiveProps,
} from '@customerio/pluma-components/react';
import { useIsFullScreenAssistantPage, useCopilotSessionContext } from '@/features/copilot';
import { open as openSidebar } from '@/features/sidebar';

import { servicesQuery } from '@/services/api';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useCurrentWorkspace } from '@/context/current-workspace';
import * as styles from './index.css';
import type { SearchResult } from '@/services/auth/types';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { createModal } from '@/hooks/system/create-modal';
import { LABEL } from './constants';
import { getIconNameFromSearchResult } from './utils';
import { useStoredHydraCommands } from './use-stored-hydra-commands';
import { SEARCH_RESULT_HEADINGS } from './constants';
import { groupSearchResults } from './utils';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { PlumaModal as PlumaModalCss } from '@customerio/pluma-components/css/components';
import { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';

export const useCommandDialog = createModal('command-dialog', {
  type: 'ephemeral',
});

export function CommandDialog() {
  const isUniversalSearchEnabled = useFeatureFlag(AccountFeatureFlag.UniversalSearchDialog);
  const isAiAssistantV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const isFullScreenAssistantPage = useIsFullScreenAssistantPage();
  const showAskAssistant = isAiAssistantV2 && !isFullScreenAssistantPage;
  const session = useCopilotSessionContext();
  const { track } = useAnalytics();

  const safeNavigate = useSafeNavigate();
  const commandDialog = useCommandDialog();
  const [inputValue, setInputValue] = React.useState('');
  const [debouncedInputValue] = useDebouncedValue(inputValue.trim(), 250);
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id ?? 0;

  const handleInputFocus = useCallback((e: Event & { target: HTMLInputElement }) => {
    e.target.select();
  }, []);

  const { data, isLoading, isError } = servicesQuery.search.useQuery({
    queryKey: ['search', debouncedInputValue, workspaceId],
    queryData: {
      params: { workspaceId },
      query: { q: debouncedInputValue },
    },
    enabled: isUniversalSearchEnabled && commandDialog.isOpen && debouncedInputValue.length > 0 && workspaceId > 0,
    staleTime: 1000 * 30, // Consider search results stale after 30 seconds
    retryOnMount: false,
  });

  const closeDialog = useCallback(() => {
    commandDialog.close();
  }, [commandDialog]);

  // Toggle the menu when ⌘K is pressed
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (commandDialog.isOpen) {
          void track('commandDialog', 'closed', { Reason: 'shortcut' });
        } else {
          void track('commandDialog', 'opened', { 'Entry Point': 'shortcut' });
        }
        commandDialog.toggle();
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [commandDialog, track, closeDialog]);

  const groupedResults = useMemo(() => groupSearchResults(data?.body.results ?? []), [data]);

  // Using inputValue instead of debouncedInputValue as we aren't calling
  // an expensive API like /search and there probably won't be many commands to filter by
  const hydraCommands = useStoredHydraCommands(inputValue);

  const handleItemSelect = useCallback(
    (url: string, kind: SearchResult['kind']) => {
      void track('commandDialog', 'itemSelected', {
        'Item Type': 'search_result',
        'Result Kind': kind,
      });
      safeNavigate(url);
      closeDialog();
    },
    [safeNavigate, closeDialog, track],
  );

  const handleLocalCommandSelect = useCallback(
    (onSelect: () => void | Promise<void>) => {
      void track('commandDialog', 'itemSelected', {
        'Item Type': 'local_command',
        'Result Kind': 'command',
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [closeDialog, track],
  );

  const handleAskAssistant = useCallback(() => {
    void track('commandDialog', 'itemSelected', {
      'Item Type': 'ask_assistant',
      'Result Kind': 'assistant',
    });
    openSidebar('copilot');
    if (debouncedInputValue.length > 0) {
      void session.sendMessage(debouncedInputValue).catch(sentryCaptureException);
    }
    closeDialog();
  }, [closeDialog, track, debouncedInputValue, session]);

  const handleDialogClose = useCallback<NonNullable<ModalPrimitiveProps['onClose']>>(
    (_event, reason) => {
      void track('commandDialog', 'closed', { Reason: reason ?? 'dismiss' });
      closeDialog();
    },
    [closeDialog, track],
  );

  const hasNoApiResults =
    data == null || (data != null && (data.body.results == null || data.body.results.length === 0));
  const hasNoResults = hasNoApiResults && hydraCommands.length === 0;
  const hasLocalCommands = hydraCommands.length > 0;

  return (
    <ModalPrimitive
      initialFocusIndex={-1}
      animationTransitionDuration={125}
      isOpen={commandDialog.isOpen}
      onClose={handleDialogClose}
    >
      <ModalPrimitiveOverlay className={PlumaModalCss.classNames.modalOverlay}>
        <ModalPrimitiveDialog className={styles.dialog}>
          <Command loop label={LABEL} shouldFilter={false}>
            <Command.Input asChild value={inputValue} onValueChange={setInputValue}>
              <TextField
                autoFocus={true}
                placeholder={LABEL}
                onInput={(e) => setInputValue(e.target.value)}
                onFocus={handleInputFocus}
                ariaLabel="Use the command palette to search for campaigns, templates, segments, or anything else in this workspace."
                className={`${styles.largeTextField} fs-mask`}
                icon="search"
                size="sm"
                ml="200"
                mr={hasNoResults ? '200' : undefined}
                mt="100"
              />
            </Command.Input>
            <Command.List>
              {showAskAssistant && !(debouncedInputValue.length > 0 && !isLoading && (hasNoResults || isError)) ? (
                <Command.Group heading="Assistant">
                  <Command.Item key="ask-assistant" value="ask-assistant" onSelect={handleAskAssistant} asChild>
                    <Box alignItems="flex-start" gap="050" display="flex" className={styles.assistantItem}>
                      <Icon mt="025" size="sm" sizeVersion="v2" color="base" name="sparkle" />
                      <Flex direction="column">
                        <Text text="product-p">Ask Assistant</Text>
                        <Text size="xxs" color="subtle">
                          {debouncedInputValue.length > 0 ? (
                            <>
                              Send &ldquo;<span className="fs-mask">{debouncedInputValue}</span>&rdquo; to the AI
                              Assistant
                            </>
                          ) : (
                            'Ask the AI Assistant a question'
                          )}
                        </Text>
                      </Flex>
                    </Box>
                  </Command.Item>
                </Command.Group>
              ) : null}
              {hasLocalCommands ? (
                <>
                  <Command.Group heading="Commands">
                    {hydraCommands.map((cmd) => (
                      <Command.Item
                        key={`local-${cmd.id}`}
                        value={`local-${cmd.id}`}
                        onSelect={() => handleLocalCommandSelect(cmd.onSelect)}
                        asChild
                      >
                        <Box alignItems="flex-start" gap="050" display="flex">
                          <Icon
                            mt="025"
                            size="sm"
                            sizeVersion="v2"
                            color="base"
                            name={(cmd.icon ?? 'command') as IconName}
                          />
                          <Flex direction="column">
                            <Text text="product-p">{cmd.label}</Text>
                            {cmd.description != null ? (
                              <Text size="xxs" color="subtle">
                                {cmd.description}
                              </Text>
                            ) : null}
                          </Flex>
                        </Box>
                      </Command.Item>
                    ))}
                  </Command.Group>
                  {isLoading ? (
                    <Command.Loading asChild>
                      <EmptyState>
                        <EmptyStateGraphic>
                          <Spinner className={styles.spinner} color="information" />
                        </EmptyStateGraphic>
                        <EmptyStateTitle>Searching in {currentWorkspace?.name ?? 'this workspace'}…</EmptyStateTitle>
                      </EmptyState>
                    </Command.Loading>
                  ) : null}
                </>
              ) : null}
              {!hasLocalCommands && isLoading ? (
                <Command.Loading asChild>
                  <EmptyState>
                    <EmptyStateGraphic>
                      <Spinner className={styles.spinner} color="information" />
                    </EmptyStateGraphic>
                    <EmptyStateTitle>
                      Searching for "<span className="fs-mask">{debouncedInputValue}</span>"
                    </EmptyStateTitle>
                    <EmptyStateDescription>
                      Hang on while we search in {currentWorkspace?.name ?? 'this workspace'}
                    </EmptyStateDescription>
                  </EmptyState>
                </Command.Loading>
              ) : null}
              {debouncedInputValue.length === 0 && !hasLocalCommands ? (
                <Command.Empty asChild>
                  <EmptyState>
                    <EmptyStateTitle icon="search">Search {currentWorkspace?.name ?? 'Workspace'}</EmptyStateTitle>
                    <EmptyStateDescription>
                      Try searching for campaigns, templates, segments, or anything else in this workspace.
                    </EmptyStateDescription>
                  </EmptyState>
                </Command.Empty>
              ) : null}
              {debouncedInputValue.length > 0 && !isLoading && (hasNoResults || isError) ? (
                showAskAssistant ? (
                  <Command.Group heading="Assistant">
                    <Command.Item key="ask-assistant" value="ask-assistant" onSelect={handleAskAssistant} asChild>
                      <Box alignItems="flex-start" gap="050" display="flex" className={styles.assistantItem}>
                        <Icon mt="025" size="sm" sizeVersion="v2" color="base" name="sparkle" />
                        <Flex direction="column">
                          <Text text="product-p">No results found</Text>
                          <Text size="xxs" color="subtle">
                            Send &ldquo;<span className="fs-mask">{debouncedInputValue}</span>&rdquo; to the AI
                            Assistant
                          </Text>
                        </Flex>
                      </Box>
                    </Command.Item>
                  </Command.Group>
                ) : (
                  <Command.Empty asChild>
                    <EmptyState>
                      <EmptyStateTitle icon="search">
                        No results found for <span className="fs-mask">{debouncedInputValue}</span>
                      </EmptyStateTitle>
                      <EmptyStateDescription>Try adjusting your search to find matches.</EmptyStateDescription>
                    </EmptyState>
                  </Command.Empty>
                )
              ) : null}
              {data?.body.results != null ? (
                <>
                  {groupedResults.map(([kind, items]) => (
                    <Command.Group
                      key={kind}
                      heading={SEARCH_RESULT_HEADINGS?.[kind] ?? `Search Results for '${kind}'`}
                    >
                      {items.map((item, index) => {
                        const url = new URL(item.url);
                        const safeUrlValue = url.pathname + url.search;
                        const hasDescription = item.kind === 'campaign' || item.kind === 'segment';
                        const key = `${item.kind}-${index}-${item.doc_id ?? encodeURIComponent(item.url)}`;

                        return (
                          <Command.Item
                            onSelect={() => handleItemSelect(safeUrlValue, item.kind)}
                            key={key}
                            value={key}
                            asChild
                          >
                            <Box alignItems="flex-start" gap="050" display="flex">
                              <Icon
                                mt="025"
                                name={getIconNameFromSearchResult(item)}
                                size="sm"
                                sizeVersion="v2"
                                color="base"
                              />
                              <Flex direction="column" gap="025">
                                <Flex direction="column">
                                  <Text text="product-p" className="fs-mask">
                                    {item.title}
                                  </Text>
                                  {hasDescription && item.other?.description != null ? (
                                    <Text size="xxs" color="subtle" className="fs-mask">
                                      {item.other.description}
                                    </Text>
                                  ) : null}
                                </Flex>
                                {item.other?.created_at != null && item.other.created_at != null ? (
                                  <>
                                    <Text size="xxs" color="subtle">
                                      Created at{' '}
                                      <FormattedDateTime
                                        className="fs-mask"
                                        date={item.other.created_at}
                                        format="medium"
                                      />
                                    </Text>
                                  </>
                                ) : null}
                              </Flex>
                            </Box>
                          </Command.Item>
                        );
                      })}
                    </Command.Group>
                  ))}
                </>
              ) : null}
            </Command.List>
          </Command>
          <InlineStack
            w="full"
            alignInline="space-between"
            py="100"
            px="200"
            alignBlock="center"
            className={styles.footer}
          >
            <Flex gap="050" alignItems="center">
              <KeyboardShortcut hotkey="Escape" />
              <Text size="xxs" color="subtle">
                Close
              </Text>
            </Flex>

            <InlineStack alignInline="space-between" gap="300">
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="ArrowUp" />
                <KeyboardShortcut hotkey="ArrowDown" />
                <Text size="xxs" color="subtle">
                  Navigate
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Enter" />
                <Text size="xxs" color="subtle">
                  Select
                </Text>
              </Flex>
            </InlineStack>
          </InlineStack>
        </ModalPrimitiveDialog>
      </ModalPrimitiveOverlay>
    </ModalPrimitive>
  );
}
